import { ServiceListDataType } from '../../../@types/service.type';
import { parseSLASummaryString } from '../../../components/molecules/SLACircleIndicator';
import { ColumnSelectionItem } from '../../../components/templates/ColumnSelection';
import { columnSelectionItems } from '../../../constants/service.constant';

// Fields required for mobile view
export const MOBILE_REQUIRED_FIELDS = [
  'serviceId',
  'serviceTypeName',
  'actionTypeName',
  'serviceZone',
  'serviceStatusName',
  'primaryAllocation',
  'primaryAllocationNode',
  'scheduledDate',
  'locationName',
  'locationZone',
  'nodeReference1',
  'nodeReference2',
  'nodeReference3',
  'reportedModel',
  'reportedFaultDescription',
  'reportedFaultDescriptionCombine',
  'partTypeName',
  'priorityCode',
  'clientSLASummary',
  'opsSLASummary',
  'closedAt',
  'clientHierarchy',
  'tags',
  'creationNotes',
  'reference1',
  'reference2',
  'reference3',
];

/**
 * Create mobile-specific column selection with required fields enabled
 */
export const getMobileColumnSelection = (): ColumnSelectionItem[] => {
  return columnSelectionItems.map((group: ColumnSelectionItem) => ({
    ...group,
    value: group.value.map((column: { field: string; title: string; value: boolean }) => ({
      ...column,
      value: MOBILE_REQUIRED_FIELDS.includes(column.field) ? true : column.value,
    })),
  }));
};

/**
 * Calculate if service is urgent (SLA breached or <4h remaining — CMS1 urgency 1)
 */
export const isServiceUrgent = (service: ServiceListDataType): boolean => {
  const slaString = service.clientSLASummary || service.opsSLASummary;
  if (!slaString) return false;
  const slaData = parseSLASummaryString(slaString);
  if (!slaData?.timeoutAt) return false;

  const outcome = slaData.slaOutcome?.outcomeTypeCode;
  if (outcome === 'breach') return true;

  const now = new Date();
  const timeout = new Date(slaData.timeoutAt);
  const diffMins = Math.floor((timeout.getTime() - now.getTime()) / 60000);

  return diffMins < 240 && diffMins >= 0; // <4h = urgency 1 (red)
};

/**
 * Check if a date string represents today
 */
export const isDateToday = (dateString: string | undefined): boolean => {
  if (!dateString) return false;
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return false;
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  } catch {
    return false;
  }
};

/**
 * Check if service is scheduled for today
 */
export const isServiceToday = (service: ServiceListDataType): boolean => {
  return isDateToday(service.scheduledDate);
};

export type SortOption = 'newest' | 'oldest' | 'sla' | 'mostUrgent' | 'zone';

/**
 * Get SLA time remaining in milliseconds (Infinity if no SLA)
 */
const getSLATimeRemaining = (service: ServiceListDataType): number => {
  const slaString = service.clientSLASummary || service.opsSLASummary;
  if (!slaString) return Infinity;
  const slaData = parseSLASummaryString(slaString);
  if (!slaData?.timeoutAt) return Infinity;
  return new Date(slaData.timeoutAt).getTime() - Date.now();
};

/**
 * Get numeric priority rank (lower = more urgent). P1=1, P2=2, etc.
 */
const getPriorityRank = (service: ServiceListDataType): number => {
  const code = service.priorityCode;
  if (!code) return 99;
  const match = code.match(/(\d+)/);
  return match ? parseInt(match[1], 10) : 99;
};

/**
 * Sort services by the selected sort option
 */
export const sortServices = (
  services: ServiceListDataType[],
  sortOption: SortOption
): ServiceListDataType[] => {
  const sorted = [...services];
  switch (sortOption) {
    case 'newest':
      return sorted.sort((a, b) => {
        const dateA = a.scheduledDate ? new Date(a.scheduledDate).getTime() : 0;
        const dateB = b.scheduledDate ? new Date(b.scheduledDate).getTime() : 0;
        return dateB - dateA;
      });
    case 'oldest':
      return sorted.sort((a, b) => {
        const dateA = a.scheduledDate ? new Date(a.scheduledDate).getTime() : 0;
        const dateB = b.scheduledDate ? new Date(b.scheduledDate).getTime() : 0;
        return dateA - dateB;
      });
    case 'sla':
      return sorted.sort((a, b) => getSLATimeRemaining(a) - getSLATimeRemaining(b));
    case 'mostUrgent':
      return sorted.sort((a, b) => getPriorityRank(a) - getPriorityRank(b));
    case 'zone':
      return sorted.sort((a, b) => {
        const zoneA = (a.serviceZone || a.locationZone || '').toLowerCase();
        const zoneB = (b.serviceZone || b.locationZone || '').toLowerCase();
        return zoneA.localeCompare(zoneB);
      });
    default:
      return sorted;
  }
};

/**
 * Filter services by closed date range
 */
export const filterByClosedDate = (
  services: ServiceListDataType[],
  filter: 'closed7Days' | 'closed30Days' | string
): ServiceListDataType[] => {
  if (filter !== 'closed7Days' && filter !== 'closed30Days') {
    return services;
  }

  const now = new Date();
  const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

  return services.filter((service) => {
    if (!service.closedAt) return false;
    const closedDate = new Date(service.closedAt);

    if (filter === 'closed7Days') return closedDate >= sevenDaysAgo;
    if (filter === 'closed30Days') return closedDate >= thirtyDaysAgo;
    return true;
  });
};
